function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// eslint-disable-next-line no-restricted-imports
import { jsx } from 'panel';
import produce from 'immer';

// @ts-strict-ignore
import { MetricsResource } from 'common/report/model-resources/metrics';
import { ActionType, ContextPanelData, DetailType } from 'common/widgets/context-panel/types';
import { ModelStatus } from 'common/util/model-resource/model-resource';
import { getEditCapability, getShareCapability } from 'common/report/util/saved-entities';
import { EntityType } from 'common/report/util/saved-entities/types';
import { getProjectId, getReportUrl, getWorkspaceId } from 'common/report/util';
import { getBaseColorFromMetricType, getIconForSavedMetric, getMetricBehaviorType, MetricTypeIcons } from 'common/report/insights/util/metrics';
import { getCountByForMetric, getSelectedCountByLabel } from 'common/query-builder/query-block/count-menu/util';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { LocalizationStore } from 'common/i18n/store';
import { BehaviorDefinitionPanelEl, Props as BehaviorDefinitionPanelProps } from 'iron/common/widgets/context-panel/behavior-definition-panel';
import { rgbaStringFromHexString } from 'common/util/colors';
import { isSaveableBehavior } from 'common/report/insights/contexts/utils/behaviors';
import { BehaviorMetric } from 'app_api/projects/metrics/types';
import { BehaviorsResource } from 'common/report/model-resources/behaviors';
import { getSelectedGroupLabel } from 'common/query-builder/query-entry-section/util';
import type { ProjectUsersResource } from 'common/report/model-resources/project-users';
import { isCanvasEnabled } from 'common/report/util/feature-sampling';
import { CurrentProject } from 'common/report/model-resources/projects';
import { SavedMetricMenuItemValue } from 'common/query-builder/query-block/multi-metrics-menu/types';
import { MetricType } from 'common/types/reports/bookmark';
function getBackgroundColorForMetric(metricType: MetricType) {
  return rgbaStringFromHexString(getBaseColorFromMetricType(metricType), 0.04);
}

/**
 * Generates the context panel data for Metrics to allow for sharing and description editing
 */
export function generateSavedMetricContextPanelData(_ref: {
  item: SavedMetricMenuItemValue;
  metricsContext: MetricsResource;
  behaviorsContext: BehaviorsResource;
  dataGroups: DataGroup[];
  i18n: LocalizationStore;
  projectUsers: ProjectUsersResource;
  currentProject: CurrentProject;
  canWriteMetrics?: boolean;
  canShare?: boolean;
}): ContextPanelData {
  var _metric, _metric$last_verified, _metric$last_verified2;
  let {
    item,
    metricsContext,
    behaviorsContext,
    dataGroups,
    i18n,
    projectUsers,
    currentProject,
    canWriteMetrics = false,
    canShare = false
  }: {
    item: SavedMetricMenuItemValue;
    metricsContext: MetricsResource;
    behaviorsContext: BehaviorsResource;
    dataGroups: DataGroup[];
    i18n: LocalizationStore;
    projectUsers: ProjectUsersResource;
    currentProject: CurrentProject;
    canWriteMetrics?: boolean;
    canShare?: boolean;
  } = _ref;
  if (!item.id) {
    const metricType = item.definition.behavior.type;
    return {
      header: item.name,
      actions: [],
      links: [],
      details: [],
      subHeader: _ref2 => {
        let {
          t
        } = _ref2;
        return t("query-builder.metrics.unsaved-metric");
      },
      entityIcon: MetricTypeIcons[metricType],
      backgroundColor: getBackgroundColorForMetric(metricType),
      description: item.description,
      entityType: EntityType.Metric,
      excludedMetricIcons: ["pie", "pulse"]
    };
  }
  const canvasEnabled = isCanvasEnabled(currentProject);
  const model = metricsContext.getOne(item.id);
  let metric = model.value as BehaviorMetric | undefined;

  // metric.definition can be undefined if it's a cached metric that's been deleted.
  // see https://mixpanel.atlassian.net/browse/AE-1494
  if (model.status === ModelStatus.Loading || !((_metric = metric) !== null && _metric !== void 0 && _metric.definition)) {
    return null;
  }
  const permissions = getEditCapability(metric);
  const actions: ContextPanelData[`actions`] = [];
  const links: ContextPanelData[`links`] = [];
  const details: ContextPanelData[`details`] = [];
  if (metric.definition.behavior.id && !metric.definition.behavior.hasUnsavedChanges) {
    const behaviorModel = behaviorsContext.getOne(metric.definition.behavior.id);
    const behavior = behaviorModel.value;
    if (behaviorModel.status !== ModelStatus.Loading && behavior) {
      metric = produce(metric, draft => {
        draft.definition.behavior = _objectSpread({
          id: behavior.id,
          name: behavior.name
        }, behavior.definition.behavior);
      });
    }
  }
  const metricBehavior = metric.definition.behavior;

  // manage sharing and permissions action
  if (canShare) {
    actions.push({
      name: ActionType.ShareMetric,
      label: _ref3 => {
        let {
          t
        } = _ref3;
        return t("query-builder.context-menu.share");
      },
      icon: "invite",
      // Only superadmin or editor can share
      disabled: !getShareCapability(metric, EntityType.Metric).enableShare,
      disabledTooltipText: _ref4 => {
        let {
          t
        } = _ref4;
        return t("query-builder.context-menu.share-no-access");
      },
      item: {
        id: metric.id,
        superadminOverrideShare: getShareCapability(metric).requireSuperadminOverride
      }
    });
  }

  // view entity in lexicon link
  links.push({
    icon: "share",
    href: getReportUrl("lexicon", {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId()
    }) + "/transformations/metrics/".concat(metric.id),
    tooltipText: _ref5 => {
      let {
        t
      } = _ref5;
      return t("query-builder.context-menu.view-in-lexicon");
    }
  });
  actions.push({
    name: ActionType.ViewEntity,
    label: _ref6 => {
      let {
        t
      } = _ref6;
      return t("query-builder.context-menu.view-in-lexicon");
    },
    icon: "share",
    item: {
      id: metric.id,
      name: metric.name,
      url: getReportUrl("lexicon", {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId()
      }) + "/transformations/metrics/".concat(metric.id)
    }
  });
  if (metric.created_by) {
    details.push({
      name: _ref7 => {
        let {
          t
        } = _ref7;
        return t("query-builder.context-menu.creator");
      },
      type: DetailType.User,
      value: _objectSpread({}, metric.created_by)
    });
  }
  if (canvasEnabled && metric.owned_by && projectUsers.getAllValues().find(member => member.email === metric.owned_by.email)) {
    details.push({
      name: _ref8 => {
        let {
          t
        } = _ref8;
        return t("query-builder.context-menu.owner");
      },
      type: DetailType.User,
      value: _objectSpread({}, metric.owned_by)
    });
  }
  const definitionDetails: ContextPanelData[`details`] = [];
  definitionDetails.push({
    name: _ref9 => {
      let {
        t
      } = _ref9;
      return t("query-builder.context-menu.data-group");
    },
    value: i18n.print(getSelectedGroupLabel({
      dataGroupId: metric.definition.measurement.dataGroupId,
      dataGroups
    }))
  });
  definitionDetails.push({
    name: _ref10 => {
      let {
        t
      } = _ref10;
      return t("query-builder.context-menu.measurement");
    },
    value: i18n.print(getSelectedCountByLabel({
      selected: getCountByForMetric(metric),
      metricType: metricBehavior.type,
      dataGroupId: metric.definition.measurement.dataGroupId,
      dataGroups,
      withDataGroupSelect: true
    }))
  });
  let definitionRenderer: ContextPanelData[`definitionRenderer`];
  // exclude things like user profiles and cohorts
  if (isSaveableBehavior(metricBehavior)) {
    definitionRenderer = () => {
      return jsx(BehaviorDefinitionPanelEl["panelTagName"] as string, {
        params: {
          props: {
            behavior: metricBehavior,
            excludeBehaviorName: false
          } satisfies BehaviorDefinitionPanelProps
        }
      });
    };
  }
  return {
    actions,
    links,
    header: metric.name,
    subHeader: _ref11 => {
      let {
        t
      } = _ref11;
      return t("query-builder.metrics.saved-metric");
    },
    entityIcon: getIconForSavedMetric(metric),
    backgroundColor: getBackgroundColorForMetric(getMetricBehaviorType(metric)),
    description: metric.description,
    entityType: EntityType.Metric,
    excludedMetricIcons: ["pie", "pulse"],
    definitionDetails,
    definitionRenderer,
    details,
    // allows for editing the description of the metric
    editItem: canWriteMetrics && permissions.enableEdit ? {
      id: metric.id,
      type: EntityType.Metric,
      name: metric.name
    } : null,
    verified: metric.verified,
    verifiedAt: metric.last_verified,
    verifiedBy: ((_metric$last_verified = metric.last_verified_by) === null || _metric$last_verified === void 0 ? void 0 : _metric$last_verified.name) || ((_metric$last_verified2 = metric.last_verified_by) === null || _metric$last_verified2 === void 0 ? void 0 : _metric$last_verified2.email)
  };
}